import { isPast, isToday } from "date-fns";
import { AlertCircle, MoreVerticalIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import {
  useDeleteRecurringSession,
  useDeleteSession,
} from "../api/delete-session";

type SessionActionsProps = {
  sessionId: string;
  endDate: Date;
  className?: string;
  recurringId?: string;
};

export function SessionActions({
  sessionId,
  endDate,
  className,
  recurringId,
}: SessionActionsProps) {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const navigate = useNavigate();
  const {
    mutateAsync: deleteSession,
    isPending: isDeletingSession,
    isSuccess: isDeletedSession,
    isError: isErrorSession,
    error: sessionError,
  } = useDeleteSession();

  const {
    mutateAsync: deleteRecurringSession,
    isPending: isDeletingRecurring,
    isSuccess: isDeletedRecurring,
    isError: isErrorRecurring,
    error: recurringError,
  } = useDeleteRecurringSession();

  const isDeleting = isDeletingRecurring || isDeletingSession;
  const isDeleted = isDeletedRecurring || isDeletedSession;
  const isError = isErrorRecurring || isErrorSession;
  const error = recurringError || sessionError;

  useEffect(() => {
    if (isDeleted) {
      navigate("/sessions");
    }
  }, [isDeleted, navigate]);

  const isRecurring = !!recurringId;

  const handleDelete = () => {
    deleteSession({ sessionId });
  };

  const handleDeleteRecurring = () => {
    deleteRecurringSession({ sessionId });
  };

  const isPastSession = useMemo(() => {
    return isPast(endDate) && !isToday(endDate);
  }, [endDate]);

  return (
    <Dialog onOpenChange={setIsDeleteModalOpen} open={isDeleteModalOpen}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild className={cn(className)}>
          <Button variant="ghost" size="icon" aria-label="Session actions">
            <MoreVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isPastSession && (
            <DropdownMenuItem asChild>
              <Link to={`/sessions/${sessionId}/edit`}>Edit</Link>
            </DropdownMenuItem>
          )}

          <DialogTrigger asChild>
            <DropdownMenuItem className="text-destructive">
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Are you absolutely sure?</DialogTitle>
          <DialogDescription>
            This action cannot be undone. Are you sure you want to permanently
            delete this session?
          </DialogDescription>
        </DialogHeader>
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error?.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          {isRecurring && (
            <Button
              type="submit"
              variant="outline"
              className="mr-auto"
              onClick={handleDeleteRecurring}
              disabled={isDeleting || isDeleted}
              isLoading={isDeletingRecurring || isDeletedRecurring}
              isAnimated
            >
              Delete All Sessions
            </Button>
          )}
          <DialogClose asChild>
            <Button variant="outline" disabled={isDeleting} autoFocus>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting || isDeleted}
            isLoading={isDeletingSession}
            isAnimated
          >
            Delete this session
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
